import React, { useEffect, useMemo, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import { useParams, useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FaArrowLeft, FaCalendarAlt, FaUser, FaFileAlt, FaDownload, FaPrint, FaFileCode,
  FaChartBar, FaBalanceScale, FaStream, FaUsers, FaPlusCircle, FaMinusCircle,
} from 'react-icons/fa';
import { CitePopup, wrapWithCitations } from '../UI/Citations';
import {
  TechShell, TechPageHeader, TechSection, TechButton, TechButtonGhost, TechChip,
  Loading, ErrorBox, borderSoft, panelDeep, mono, accent,
} from '../../styles/techComponents';
import { CATEGORY_META } from './data';
import { downloadMarkdown, downloadJSON, printItem } from './download';
import { getResearchItem } from '../../services/researchService';

/* ---------- estilos ---------- */
const Excerpt = styled.div`
  background: ${panelDeep}; border: 1px solid ${borderSoft}; border-left: 2px solid ${accent};
  border-radius: 4px; padding: 0.95rem 1.1rem; color: var(--text-secondary);
  font-size: 0.98rem; line-height: 1.6; margin-bottom: 1.3rem; font-style: italic;
`;
const Tag = styled.span`
  font-family: ${mono}; font-size: 0.68rem; color: #cfe9f4; background: rgba(0,180,216,0.06);
  border: 1px solid rgba(0,180,216,0.22); border-radius: 3px; padding: 1px 7px;
  &::before { content: '#'; color: ${accent}; opacity: 0.85; margin-right: 1px; }
`;
const TagRow = styled.div`display: flex; flex-wrap: wrap; gap: 5px; margin-top: 0.6rem;`;
const MetaRow = styled.div`
  display: flex; gap: 1rem; flex-wrap: wrap; font-family: ${mono}; font-size: 0.68rem;
  letter-spacing: 0.14em; text-transform: uppercase; color: var(--text-disabled); margin-top: 0.5rem;
  span { display: inline-flex; align-items: center; gap: 0.4rem; } svg { color: ${accent}; }
`;
const SectionTitle = styled.h3`
  display: flex; align-items: center; gap: 0.5rem; margin: 0 0 0.9rem;
  font-size: 0.82rem; text-transform: uppercase; letter-spacing: 0.14em; color: ${accent};
  svg { font-size: 0.9rem; }
`;
const Metrics = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 0.75rem; margin-bottom: 1.4rem;
`;
const Metric = styled.div`
  background: ${panelDeep}; border: 1px solid ${borderSoft}; border-left: 2px solid ${accent};
  border-radius: 6px; padding: 0.8rem 0.9rem;
  .v { font-size: 1.5rem; font-weight: 700; color: var(--text-primary); font-family: ${mono}; }
  .l { font-size: 0.62rem; letter-spacing: 0.14em; text-transform: uppercase; color: var(--text-disabled); margin-top: 0.2rem; }
`;
const Bars = styled.div`display: flex; flex-direction: column; gap: 0.6rem;`;
const BarRow = styled.div`
  display: grid; grid-template-columns: 190px 1fr 34px; align-items: center; gap: 0.7rem;
  .lbl { font-size: 0.78rem; color: var(--text-secondary); }
  .track { height: 14px; background: rgba(255,255,255,0.05); border-radius: 7px; overflow: hidden; }
  .fill { height: 100%; border-radius: 7px; transition: width 0.5s ease; }
  .num { font-family: ${mono}; font-size: 0.8rem; color: var(--text-primary); text-align: right; }
  @media (max-width: 640px) { grid-template-columns: 110px 1fr 28px; }
`;
const ImpactGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;
  @media (max-width: 760px) { grid-template-columns: 1fr; }
`;
const ImpactCard = styled.div`
  background: ${panelDeep}; border: 1px solid ${borderSoft}; border-radius: 8px; padding: 1rem 1.1rem;
  h4 { margin: 0 0 0.7rem; color: var(--text-primary); font-size: 1rem; }
  ul { list-style: none; margin: 0 0 0.6rem; padding: 0; display: flex; flex-direction: column; gap: 0.35rem; }
  li { display: flex; gap: 0.5rem; font-size: 0.85rem; color: var(--text-secondary); line-height: 1.45; }
  .pos svg { color: var(--alert-success, #38d39f); margin-top: 2px; flex-shrink: 0; }
  .neg svg { color: var(--alert-error, #ff5470); margin-top: 2px; flex-shrink: 0; }
  .neto { margin-top: 0.6rem; padding-top: 0.6rem; border-top: 1px dashed ${borderSoft};
          font-size: 0.8rem; color: var(--text-disabled); font-style: italic; }
`;
const Timeline = styled.div`
  position: relative; margin-left: 0.5rem; padding-left: 1.2rem;
  border-left: 2px solid ${borderSoft};
  display: flex; flex-direction: column; gap: 0.9rem;
`;
const TLItem = styled.div`
  position: relative;
  &::before { content: ''; position: absolute; left: calc(-1.2rem - 6px); top: 4px; width: 9px; height: 9px;
    border-radius: 50%; background: ${accent}; box-shadow: 0 0 0 3px rgba(0,180,216,0.15); }
  .d { font-family: ${mono}; font-size: 0.64rem; letter-spacing: 0.12em; text-transform: uppercase; color: ${accent}; }
  .t { font-size: 0.86rem; color: var(--text-secondary); margin-top: 2px; line-height: 1.45; }
`;
const Actors = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 0.6rem;`;
const ActorCard = styled.div`
  background: ${panelDeep}; border: 1px solid ${borderSoft}; border-radius: 6px; padding: 0.6rem 0.8rem;
  .n { font-weight: 600; color: var(--text-primary); font-size: 0.88rem; }
  .r { font-size: 0.76rem; color: var(--text-disabled); margin-top: 2px; line-height: 1.4; }
`;
const Markdown = styled.div`
  color: var(--text-secondary); line-height: 1.8; font-size: 1rem; max-width: 800px;
  & > *:first-child { margin-top: 0; }
  h1,h2,h3,h4 { color: var(--text-primary); font-weight: 650; line-height: 1.32; }
  h1 { font-size: 1.5em; margin: 1.6em 0 0.6em; }
  h2 {
    font-size: 1.22em; margin: 2.3em 0 0.95em; padding-bottom: 0.4em;
    border-bottom: 1px solid ${borderSoft}; display: flex; align-items: center; gap: 0.55em;
  }
  h2::before { content: ''; flex: none; width: 4px; height: 1.05em; background: ${accent}; border-radius: 2px; }
  h3 {
    font-size: 0.8em; text-transform: uppercase; letter-spacing: 0.14em;
    color: ${accent}; margin: 1.9em 0 0.65em;
  }
  h4 { font-size: 0.96em; margin: 1.4em 0 0.5em; }
  p { margin: 0 0 1.1em; }
  ul, ol { margin: 0.5em 0 1.25em; padding-left: 1.5em; }
  li { margin-bottom: 0.6em; padding-left: 0.25em; }
  li::marker { color: ${accent}; }
  li > ul, li > ol { margin: 0.5em 0 0.3em; }
  strong { color: var(--text-primary); font-weight: 650; }
  em { color: var(--text-primary); font-style: italic; }
  a { color: ${accent}; text-decoration: none; border-bottom: 1px solid rgba(0,180,216,0.4); }
  a:hover { border-bottom-color: ${accent}; }
  blockquote {
    margin: 1.5em 0; padding: 0.95em 1.15em; border-left: 3px solid ${accent};
    background: rgba(0,180,216,0.06); border-radius: 0 8px 8px 0; color: var(--text-secondary);
  }
  blockquote p { margin: 0; }
  hr { border: 0; border-top: 1px solid ${borderSoft}; margin: 2.4em 0; }
  code { font-family: ${mono}; font-size: 0.86em; padding: 2px 6px; background: rgba(0,0,0,0.4); border-radius: 4px; color: #cfe9f4; }
  table { width: 100%; border-collapse: collapse; margin: 1.3em 0; font-size: 0.92em; }
  th, td { padding: 8px 12px; border: 1px solid ${borderSoft}; text-align: left; vertical-align: top; }
  th { background: rgba(255,255,255,0.04); font-family: ${mono}; font-size: 0.72em; letter-spacing: 0.12em; text-transform: uppercase; color: var(--text-disabled); }
  tr:nth-child(even) td { background: rgba(255,255,255,0.015); }
`;

/* Extrae un bloque ```arcana-analytics { ... } ``` del cuerpo. Devuelve { analytics, rest }. */
function parseAnalytics(body) {
  if (!body) return { analytics: null, rest: '' };
  const m = body.match(/```arcana-analytics\s*([\s\S]*?)```/);
  if (!m) return { analytics: null, rest: body };
  let analytics = null;
  try { analytics = JSON.parse(m[1].trim()); } catch { analytics = null; }
  const rest = body.replace(m[0], '').trim();
  return { analytics, rest };
}

// Parte el markdown en secciones por encabezado H2 (## ). Devuelve [{title, md}].
// El contenido antes del primer ## queda como "Introducción".
function splitSections(md) {
  if (!md) return [];
  const lines = md.split('\n');
  const sections = [];
  let cur = { title: 'Introducción', body: [] };
  for (const line of lines) {
    const m = line.match(/^##\s+(.+?)\s*$/);
    if (m) {
      if (cur.body.join('\n').trim() || cur.title !== 'Introducción') sections.push(cur);
      cur = { title: m[1].trim(), body: [] };
    } else {
      cur.body.push(line);
    }
  }
  if (cur.body.join('\n').trim() || sections.length === 0) sections.push(cur);
  return sections
    .map((s) => ({ title: s.title, md: s.body.join('\n').trim() }))
    .filter((s) => s.md || s.title !== 'Introducción');
}

const SecTabs = styled.div`
  display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 1.1rem;
  border-bottom: 1px solid ${borderSoft}; padding-bottom: 0.6rem;
`;
const SecTab = styled.button`
  font-family: ${mono}; font-size: 0.72rem; letter-spacing: 0.04em;
  padding: 0.4rem 0.75rem; border-radius: 5px; cursor: pointer;
  border: 1px solid ${(p) => (p.$active ? accent : borderSoft)};
  background: ${(p) => (p.$active ? 'rgba(0,180,216,0.12)' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--text-primary)' : 'var(--text-secondary)')};
  transition: all 0.15s ease;
  &:hover { border-color: ${accent}; color: var(--text-primary); }
  .n { color: ${accent}; margin-right: 5px; opacity: 0.8; }
`;

const AnalyticsDashboard = ({ a }) => {
  if (!a) return null;
  const barMax = a.framing?.bars?.length ? Math.max(...a.framing.bars.map((b) => b.value || 0), 1) : 1;
  return (
    <>
      {Array.isArray(a.metrics) && a.metrics.length > 0 && (
        <Metrics>
          {a.metrics.map((mm, i) => (
            <Metric key={i}><div className="v">{mm.value}</div><div className="l">{mm.label}</div></Metric>
          ))}
        </Metrics>
      )}

      {a.framing?.bars?.length > 0 && (
        <TechSection style={{ marginBottom: '1.2rem' }}>
          <SectionTitle><FaChartBar /> {a.framing.title || 'Encuadre mediático'}</SectionTitle>
          <Bars>
            {a.framing.bars.map((b, i) => (
              <BarRow key={i}>
                <div className="lbl">{b.label}</div>
                <div className="track"><div className="fill" style={{ width: `${Math.round(((b.value || 0) / barMax) * 100)}%`, background: b.color || accent }} /></div>
                <div className="num">{b.value}</div>
              </BarRow>
            ))}
          </Bars>
          {a.framing.note && <p style={{ color: 'var(--text-disabled)', fontSize: '0.8rem', marginTop: '0.7rem' }}>{a.framing.note}</p>}
        </TechSection>
      )}

      {a.impact && (a.impact.chouhy || a.impact.passaglia) && (
        <TechSection style={{ marginBottom: '1.2rem' }}>
          <SectionTitle><FaBalanceScale /> Balance de impacto</SectionTitle>
          <ImpactGrid>
            {[a.impact.chouhy, a.impact.passaglia].filter(Boolean).map((c, i) => (
              <ImpactCard key={i}>
                <h4>{c.title}</h4>
                <ul>{(c.pos || []).map((t, j) => <li key={`p${j}`} className="pos"><FaPlusCircle />{t}</li>)}</ul>
                <ul>{(c.neg || []).map((t, j) => <li key={`n${j}`} className="neg"><FaMinusCircle />{t}</li>)}</ul>
                {c.neto && <div className="neto">Neto: {c.neto}</div>}
              </ImpactCard>
            ))}
          </ImpactGrid>
        </TechSection>
      )}

      {Array.isArray(a.timeline) && a.timeline.length > 0 && (
        <TechSection style={{ marginBottom: '1.2rem' }}>
          <SectionTitle><FaStream /> Línea de tiempo</SectionTitle>
          <Timeline>
            {a.timeline.map((t, i) => (
              <TLItem key={i}><div className="d">{t.date}</div><div className="t">{t.text}</div></TLItem>
            ))}
          </Timeline>
        </TechSection>
      )}

      {Array.isArray(a.actors) && a.actors.length > 0 && (
        <TechSection style={{ marginBottom: '1.2rem' }}>
          <SectionTitle><FaUsers /> Actores</SectionTitle>
          <Actors>
            {a.actors.map((ac, i) => (
              <ActorCard key={i}><div className="n">{ac.name}</div><div className="r">{ac.role}</div></ActorCard>
            ))}
          </Actors>
        </TechSection>
      )}
    </>
  );
};

const ResearchDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const [item, setItem] = useState(location.state?.item || null);
  const [loading, setLoading] = useState(!location.state?.item);
  const [error, setError] = useState(null);
  const [activeCite, setActiveCite] = useState(null);
  const [activeSec, setActiveSec] = useState(0);

  useEffect(() => {
    if (item) return;
    let alive = true;
    setLoading(true);
    getResearchItem(id)
      .then((it) => { if (alive) { setItem(it); setError(null); } })
      .catch((e) => { if (alive) setError(friendlyError(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id, item]);

  const { analytics, rest } = useMemo(() => parseAnalytics(item?.body), [item]);
  const sections = useMemo(() => splitSections(rest), [rest]);
  const secIdx = Math.min(activeSec, Math.max(sections.length - 1, 0));

  if (loading) {
    return (
      <TechShell>
        <TechPageHeader title="Investigación" dossier="RES://LIB/DOC" />
        <Loading>Cargando investigación…</Loading>
      </TechShell>
    );
  }

  if (error || !item) {
    return (
      <TechShell>
        <TechPageHeader
          title="Investigación no encontrada"
          dossier="RES://LIB/404"
          actions={<TechButtonGhost as={Link} to="/research"><FaArrowLeft /> Volver</TechButtonGhost>}
        />
        <ErrorBox>{error || 'No se pudo cargar la investigación. Abrila desde la Biblioteca.'}</ErrorBox>
      </TechShell>
    );
  }

  const cat = CATEGORY_META[item.category] || { label: item.category, color: accent };
  const citations = Array.isArray(item.citations) ? item.citations : [];
  const dateVal = item.date || item.generated_at;

  return (
    <TechShell>
      <TechPageHeader
        title={item.title}
        subtitle={item.excerpt}
        dossier={`RES://LIB/${item.id}`}
        actions={
          <>
            <TechButtonGhost as={Link} to="/research"><FaArrowLeft /> Volver</TechButtonGhost>
            <TechButtonGhost type="button" onClick={() => printItem(item)}><FaPrint /> PDF</TechButtonGhost>
            <TechButtonGhost type="button" onClick={() => downloadJSON(item)}><FaFileCode /> JSON</TechButtonGhost>
            <TechButton type="button" onClick={() => downloadMarkdown(item)}><FaDownload /> Markdown</TechButton>
          </>
        }
      >
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <TechChip>{item.type}</TechChip>
          <span style={{
            fontFamily: mono, fontSize: '0.62rem', letterSpacing: '0.18em', textTransform: 'uppercase',
            padding: '2px 7px', color: cat.color, border: `1px solid ${cat.color}55`,
            background: 'rgba(0,180,216,0.04)', borderRadius: 3,
          }}>{cat.label}</span>
          {item.source === 'custom' && <TechChip tone="ok">Propio</TechChip>}
          {typeof item.pages === 'number' && <TechChip tone="mute">{item.pages} págs</TechChip>}
        </div>
        <MetaRow>
          {dateVal && <span><FaCalendarAlt />{new Date(dateVal).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>}
          <span><FaUser />{item.author}</span>
          <span><FaFileAlt />ID #{item.id}</span>
        </MetaRow>
        {(item.tags || []).length > 0 && (
          <TagRow>{(item.tags || []).map((t) => <Tag key={t}>{t}</Tag>)}</TagRow>
        )}
      </TechPageHeader>

      {analytics && <AnalyticsDashboard a={analytics} />}

      <TechSection>
        {item.excerpt && <Excerpt>{item.excerpt}</Excerpt>}
        {sections.length > 1 && (
          <SecTabs>
            {sections.map((s, i) => (
              <SecTab key={i} type="button" $active={i === secIdx} onClick={() => setActiveSec(i)}>
                <span className="n">{String(i + 1).padStart(2, '0')}</span>{s.title}
              </SecTab>
            ))}
          </SecTabs>
        )}
        {sections.length > 0 ? (
          <Markdown>
            {sections.length > 1 && <h2 style={{ marginTop: 0 }}>{sections[secIdx].title}</h2>}
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={wrapWithCitations(setActiveCite)}>
              {sections.length > 1 ? sections[secIdx].md : rest}
            </ReactMarkdown>
          </Markdown>
        ) : (
          <p style={{ color: 'var(--text-disabled)' }}>Esta investigación aún no tiene contenido extendido.</p>
        )}
      </TechSection>

      {activeCite && (
        <CitePopup cite={activeCite} indexed={citations} fresh={[]} rag={[]} onClose={() => setActiveCite(null)} />
      )}
    </TechShell>
  );
};

export default ResearchDetail;
